import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { ThemeProvider } from './lib/ThemeContext';
import { OutletProvider, useOutlet } from './lib/OutletContext';
import { ReconciliationBadgeProvider } from './lib/ReconciliationBadgeContext';
import { Toaster } from 'sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Ingredients from './pages/Ingredients';
import Prep from './pages/Prep';
import Transfers from './pages/Transfers';
import Audits from './pages/Audits';
import PurchaseReconciliation from './pages/PurchaseReconciliation';
import Intelligence from './pages/Intelligence';
import SalesUpload from './pages/SalesUpload';
import RecipeLibrary from './pages/RecipeLibrary';
import RecipeBuilder from './pages/RecipeBuilder';
import MenuMapping from './pages/MenuMapping';
import Procurement from './pages/Procurement';
import PrepPlanning from './pages/PrepPlanning';
import OutletSelector from './components/OutletSelector';
import './App.css';
import logger from './lib/logger';
import ErrorBoundary from './components/ErrorBoundary';

// Protected Route Component - checks isAuthenticated OR token presence
const ProtectedRoute = ({ children, requiresOutlet = true }) => {
  const { isAuthenticated, loading: authLoading, checkAuthenticated } = useAuth();
  const { selectedOutlet, loading: outletLoading, needsSelection } = useOutlet();
  
  // Check both state and localStorage
  const hasAccess = isAuthenticated || checkAuthenticated();

  if (authLoading || outletLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    logger.info('[ProtectedRoute] Not authenticated (isAuthenticated:', isAuthenticated, ', token:', !!localStorage.getItem('cr_access_token'), '), redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // For outlet-scoped routes, show outlet selector if needed
  if (requiresOutlet && needsSelection && !selectedOutlet) {
    logger.info('[ProtectedRoute] Outlet selection required');
    return (
      <Layout>
        <OutletSelector />
      </Layout>
    );
  }

  logger.info('[ProtectedRoute] Authenticated, outlet:', selectedOutlet || 'none required');
  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { isAuthenticated, loading, checkAuthenticated } = useAuth();
  
  // Check both state and localStorage
  const hasAccess = isAuthenticated || checkAuthenticated();

  // Log origin on app load
  React.useEffect(() => {
    logger.info('[App] Root mounted - window.location.origin:', window.location.origin);
  }, []);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={hasAccess ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ingredients"
        element={
          <ProtectedRoute>
            <Ingredients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prep"
        element={
          <ProtectedRoute>
            <Prep />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfers"
        element={
          <ProtectedRoute>
            <Transfers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audits"
        element={
          <ProtectedRoute>
            <Audits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reconciliation"
        element={
          <ProtectedRoute>
            <PurchaseReconciliation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence"
        element={
          <ProtectedRoute>
            <Intelligence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement"
        element={
          <ProtectedRoute>
            <Procurement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prep-planning"
        element={
          <ProtectedRoute>
            <PrepPlanning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-upload"
        element={
          <ProtectedRoute>
            <SalesUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <RecipeLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/new"
        element={
          <ProtectedRoute>
            <RecipeBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/mapping"
        element={
          <ProtectedRoute>
            <MenuMapping />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <ProtectedRoute>
            <RecipeBuilder />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <OutletProvider>
            <ReconciliationBadgeProvider>
              <BrowserRouter>
                <div className="App">
                  <AppRoutes />
                  <Toaster
                    position="top-right"
                    theme="dark"
                    toastOptions={{
                      style: {
                        background: '#18181b',
                        border: '1px solid #27272a',
                        color: '#ffffff',
                      },
                    }}
                  />
                </div>
              </BrowserRouter>
            </ReconciliationBadgeProvider>
          </OutletProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
